import fileUpload from './fileUpload';

/*
 * Uploads any newly-selected File objects in the accommodation
 * form (gallery, thumbnails, video, per-unit images) to Firebase
 * Storage - the same pipeline used by the trip/pack upload flow
 * (see fileUpload.js) - and returns a copy of formData with those
 * File objects replaced by their download URLs.
 *
 * Without this step, MediaSection/UnitEditor only ever hold File
 * objects in memory: nothing in the app previously uploaded them,
 * so accommodationPayload.js's transformMedia/transformCardImages/
 * transformUnitImages silently dropped every item without a `url`,
 * and the listing would save with zero photos.
 *
 * Call this once, right before buildAccommodationPayload(), in
 * AccommodationForm's handleSubmit.
 */

const isFile = (value) => typeof File !== 'undefined' && value instanceof File;

async function resolveItem(item, folder) {
    if (!item) return item;

    if (typeof item === 'string') {
        // Already an uploaded URL.
        return item;
    }

    if (isFile(item)) {
        return fileUpload(item, 'accommodation', 'create', folder);
    }

    if (typeof item === 'object') {
        if (isFile(item.file)) {
            const url = await fileUpload(item.file, 'accommodation', 'create', folder);

            const { file, preview, ...rest } = item;

            return {
                ...rest,
                url
            };
        }

        // Already has a url (existing media) - nothing to upload.
        return item;
    }

    return item;
}

async function resolveList(list, folder) {
    if (!Array.isArray(list)) return [];

    const resolved = await Promise.all(list.map((item) => resolveItem(item, folder)));

    return resolved.filter(Boolean);
}

export default async function uploadAccommodationMedia(formData, { onProgress } = {}) {
    const units = Array.isArray(formData.units) ? formData.units : [];

    onProgress?.('Uploading gallery images...');
    const media = await resolveList(formData.media, 'gallery');

    onProgress?.('Uploading thumbnail images...');
    const cardImages = await resolveList(formData.cardImages, 'thumbnail');

    onProgress?.('Uploading video...');
    const videos = await resolveList(formData.videos, 'video');

    onProgress?.('Uploading unit images...');
    const uploadedUnits = await Promise.all(
        units.map(async (unit) => ({
            ...unit,
            images: await resolveList(unit.images, 'unitImages')
        }))
    );

    return {
        ...formData,
        media,
        cardImages,
        videos,
        units: uploadedUnits
    };
}
